// src/pages/hrDashboard/PayrollPage.jsx

// MISSING 1: You must import useMemo since you use it below
import React, { useState, useEffect, useMemo } from "react"; 
import Sidebar from "../../components/MasterSideBar";
import Topbar from "../../components/TopbarNav";
import StatsCards from "../../components/payrolls/StatsCards";
import PayrollTable from "../../components/payrolls/PayrollTable";
import AddStaffModal from "../../components/payrolls/AddStaffModal";
import ApprovalSection from "../../components/payrolls/ApprovalSection";
import { fetchPayrollList } from "../../services/payroll.service";

export default function PayrollsPage() {
  // --- 1. State Management ---
  
  // Core Data
  const [payrolls, setPayrolls] = useState([]);
  
  // Shared Filter State (Lifted to Page to sync StatsCards and PayrollTable)
  const [filterBudgetCode, setFilterBudgetCode] = useState(""); 

  const [filterPeriod, setFilterPeriod] = useState(""); 

  // Loading & UI States
  const [loadingPayrolls, setLoadingPayrolls] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [error, setError] = useState(null);

  // --- 2. Data Fetching ---

    // Helper to get unique budget codes from current payrolls ---
  const availableBudgetCodes = useMemo(() => {
    const codes = new Set();
    payrolls.forEach(p => {
        if (p.budgetCode) codes.add(p.budgetCode);
    });
    return Array.from(codes).sort();
  }, [payrolls]);

  const fetchPayrolls = async () => {
    try {
      setLoadingPayrolls(true);
      setError(null);
      const data = await fetchPayrollList();
      setPayrolls(data.payrolls || data);
      setSuccessMsg("Payrolls loaded successfully");
    } catch (err) {
      setError(err.message || "Failed to fetch payrolls.");
    } finally {
      setLoadingPayrolls(false);
    }
  };

  // Simplified refresh: only need to fetch payrolls. 
  // Stats update automatically because they are derived from 'payrolls' state.
  const refreshAll = () => {
    fetchPayrolls();
  };

  // --- 3. Handlers ---

  const handleStaffAdded = () => {
    setShowAddModal(false);
    refreshAll();
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.href = "/";
  };

  // --- 4. Effects ---

  useEffect(() => {
    refreshAll();
  }, []);

  useEffect(() => {
    let successTimer;
    if (successMsg) {
      successTimer = setTimeout(() => setSuccessMsg(""), 3000);
    }
    return () => clearTimeout(successTimer);
  }, [successMsg]);

  useEffect(() => {
    let errorTimer;
    if (error) {
      errorTimer = setTimeout(() => setError(null), 3000);
    }
    return () => clearTimeout(errorTimer);
  }, [error]);

  // --- 5. Render ---

  return (
    <div className="flex min-h-screen bg-gray-50 relative">
      {/* Sidebar */}
      <Sidebar onLogout={handleLogout} />

      {/* Main Content */}
      <div className="flex-1 md:pl-64">
        <Topbar />

        {/* Popup Notifications */}
        {successMsg && (
          <div className="fixed top-5 left-1/2 transform -translate-x-1/2 z-50">
            <div className="bg-green-500 text-white px-6 py-3 rounded shadow-lg animate-fade-in-out">
              ✅ {successMsg}
            </div>
          </div>
        )}
        {error && (
          <div className="fixed top-5 left-1/2 transform -translate-x-1/2 z-50">
            <div className="bg-red-500 text-white px-6 py-3 rounded shadow-lg animate-fade-in-out">
              ❌ {error}
            </div>
          </div>
        )}

        <main className="pt-20 md:pt-24 px-3 sm:px-6 lg:px-8 pb-8 overflow-y-auto max-h-screen">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
            <h2 className="text-base sm:text-xl font-bold text-gray-700">
              Payroll Dashboard
            </h2>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-orange-600 text-white px-3 sm:px-4 py-2 rounded text-sm sm:text-base hover:bg-orange-700 w-full sm:w-auto"
              >
                Add Staff to Payroll
              </button>
            </div>
          </div>

          {/* MISSING 2: The Filter UI Section */}
          {/* This section was missing from your code. It allows users to select the Budget Code. */}
          <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3 bg-white p-3 rounded shadow-sm border border-gray-200">
             <div className="flex items-center gap-2 w-full sm:w-auto">
                  <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">Filters:</span>
                  
                  {/* Budget Code Dropdown */}
                  <select 
                      value={filterBudgetCode} 
                      onChange={(e) => setFilterBudgetCode(e.target.value)}
                      className="border border-gray-300 rounded text-sm px-3 py-1.5 focus:ring-2 focus:ring-orange-500 focus:outline-none w-full sm:w-auto"
                  >
                      <option value="">All Budget Codes</option>
                      {availableBudgetCodes.map(code => (
                          <option key={code} value={code}>{code}</option>
                      ))}
                  </select>

                  {/* Clear Filters Button */}
                  {(filterPeriod || filterBudgetCode) && (
                      <button 
                          onClick={() => { setFilterPeriod(""); setFilterBudgetCode(""); }}
                          className="text-xs text-red-600 hover:underline whitespace-nowrap"
                      >
                          Clear Filters
                      </button>
                  )}
             </div>
        </div>


          {/* Stats Cards */}
          <div className="mb-6">
            <StatsCards 
              payrolls={payrolls} 
              filterPeriod={filterPeriod} 
              filterBudgetCode={filterBudgetCode} 
            />
          </div>

          {/* Payroll Table */}
          <div className="overflow-x-auto bg-white shadow rounded mb-6">
            <PayrollTable
              payrolls={payrolls}
              loading={loadingPayrolls}
              filterPeriod={filterPeriod}
              setFilterPeriod={setFilterPeriod}
              refresh={refreshAll}
              filterBudgetCode={filterBudgetCode} 
            />
          </div>

          {/* Approval Section */}
          <div className="overflow-x-auto bg-white shadow rounded">
            <ApprovalSection 
              payrolls={payrolls} 
              refresh={refreshAll} 
            />
          </div>

          {/* Add Staff Modal */}
          {showAddModal && (
            <AddStaffModal
              isOpen={showAddModal}
              onClose={() => setShowAddModal(false)}
              onAdded={handleStaffAdded}
            />
          )}
        </main>
      </div>
    </div>
  );
}